import { ChangeEvent, useMemo, useState } from 'react';
import { useDrop } from 'react-dnd';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import { Box, SearchBar, Typography } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  DRAG_ROW_TYPE,
} from '../../../constants/campaignBuilder.js';
import { dragItemTypes } from '../../../constants/myCampaigns.js';
import { useBuyingPlanMetaChannels } from '../../../hooks/useGetBuyingPlanMetaData.js';
import useGetPlatformIntegrations from '../../../hooks/useGetPlatformIntegrations.js';
import {
  OmniConnectorFlags,
  useOmniConnectorFlags,
} from '../../../hooks/useOmniConnectorFlags.js';
import DeleteRowModal from '../../../molecules/DeleteRowModal/DeleteRowModal.jsx';
import { IntegrationsCategory, Platform } from '../../../types/integrations.js';
import { filterByOmniConnectorFlag } from '../../../utils/addRowsPanel.js';
import {
  ChannelRowType,
  DraggableItemType,
  type RowToRemoveObject,
} from '../types.js';
import ChannelBlock from './ChannelBlock/index.js';
import EmptyItem from './EmptyItem.jsx';
import TileRow from './TileRow.jsx';

export const ADD_PANEL_WIDTH = 360;

const getPreparedMediaPlanMeta = (
  metaChannels: ChannelRowType[],
  integratedPlatforms: ReturnType<
    typeof useGetPlatformIntegrations
  >['integratedPlatforms'],
  omniConnectorFlags: OmniConnectorFlags,
) => {
  if (!metaChannels) {
    return {};
  }

  const preparedChannels =
    metaChannels?.map((channel: ChannelRowType) => ({
      ...channel,
      platforms: integratedPlatforms
        .filter(
          (pl: any) =>
            pl.channels.includes(channel.name) &&
            filterByOmniConnectorFlag(pl, omniConnectorFlags),
        )
        .map((pl: any) => {
          return {
            ...pl,
            typeForAdd:
              pl.category === IntegrationsCategory.AD_NETWORK ||
              pl.category === IntegrationsCategory.PUBLISHER
                ? BUILDER_ROW_TYPES.MEDIA_NETWORK
                : BUILDER_ROW_TYPES.PLATFORM,
          };
        })
        .sort((a: Platform, b: Platform) => a.label.localeCompare(b.label)),
    })) || [];

  return {
    channels: preparedChannels,
    isNoIntegrations: !preparedChannels.find(
      (channel: any) => channel.platforms?.length,
    ),
  };
};

type Props = {
  toggleRowExpanded: (id: number, isOpen: boolean) => void;
  setRowState: (id: number[], { isNewAdded }: { isNewAdded: boolean }) => void;
};

export const AddRowsPanel = ({ toggleRowExpanded, setRowState }: Props) => {
  const { t } = useTranslation('@myn/planning');

  const [rowToDelete, setRowToDelete] = useState<RowToRemoveObject | null>(
    null,
  );
  const omniConnectorFlags = useOmniConnectorFlags();
  const { integratedPlatforms } = useGetPlatformIntegrations();
  const metaChannels = useBuyingPlanMetaChannels();

  const {
    channels,
    isNoIntegrations,
  }: { channels?: ChannelRowType[]; isNoIntegrations?: boolean } =
    getPreparedMediaPlanMeta(
      metaChannels ?? [],
      integratedPlatforms,
      omniConnectorFlags,
    ) || {};

  const [searchValue, setSearchValue] = useState<string>('');

  const filteredChannels = useMemo(() => {
    if (!searchValue) {
      return channels;
    }

    return channels?.reduce(
      (acc: ChannelRowType[], channel: ChannelRowType) => {
        const filteredPlatforms = channel?.platforms?.filter((platform) =>
          platform.label
            .replace(/ /g, '')
            .toLowerCase()
            .includes(searchValue.replace(/ /g, '').toLowerCase()),
        );
        if (!filteredPlatforms?.length) {
          return acc;
        }
        return [...acc, { ...channel, platforms: filteredPlatforms }];
      },
      [],
    );
  }, [channels, searchValue]);

  const handleDrop = (dragItem: DraggableItemType): void => {
    setRowToDelete({
      rowId: dragItem.id,
      hasChildren: dragItem.canExpand,
      campaign: dragItem.original?.campaign,
      type: dragItem.original?.type ?? '',
    });
  };

  const handleChangeSearchValue = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleResetSearchValue = () => {
    setSearchValue('');
  };

  const [{ isOver, canDrop }, drop] = useDrop({
    drop: handleDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    canDrop: (row: DraggableItemType) => Boolean(row?.original?.canBeDeleted),

    accept: [DRAG_ROW_TYPE, dragItemTypes.ADD_ROW],
  });

  const [, dropContainer] = useDrop({
    accept: [dragItemTypes.ADD_ROW, dragItemTypes.ADD_TILE],
  });

  return (
    <Wrapper>
      <Info ref={dropContainer}>
        {!isNoIntegrations && (
          <Header>
            <TileRow />
            <StyledSearchBar
              size="small"
              aria-label="Search by platform name"
              value={searchValue}
              onChange={handleChangeSearchValue}
              onClear={handleResetSearchValue}
              placeholder={t('Search by name')}
            />
          </Header>
        )}
        {isNoIntegrations || !filteredChannels?.length ? (
          <EmptyMessage>
            {!filteredChannels?.length ? (
              <Typography variant="bodyShort01">
                {t('No results found')}
              </Typography>
            ) : (
              <EmptyItem />
            )}
          </EmptyMessage>
        ) : (
          <ChannelsList ref={drop} isHighlighted={canDrop && isOver}>
            {filteredChannels?.map((channel) => (
              <ChannelBlock
                channel={channel}
                key={channel.name}
                isSearchPerformed={!!searchValue}
                toggleRowExpanded={toggleRowExpanded}
                setRowState={setRowState}
              />
            ))}
          </ChannelsList>
        )}
      </Info>
      <DeleteRowModal
        rowToRemove={rowToDelete}
        setRowToRemove={setRowToDelete}
      />
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  width: ${ADD_PANEL_WIDTH}px;
  height: 100%;
  padding-right: ${({ theme }) => theme.spacing(1.75)};
`;

const Info = styled('div')`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background: ${({ theme }) => theme.palette.ui.primary};
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding-right: ${({ theme }) => theme.spacing(1.5)};
  padding-left: ${({ theme }) => theme.spacing(0.25)};
`;

const StyledSearchBar = styled(SearchBar)`
  max-width: unset;
`;

const ChannelsList = styled('div')<{ isHighlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-left: ${({ theme }) => theme.spacing(0.25)};
  padding-right: ${({ theme }) => theme.spacing(0.5)};
  overflow: scroll;
  height: calc(100% - (182px));

  border: 1px solid
    ${({ isHighlighted }) => (isHighlighted ? 'red' : `transparent`)};
`;

const EmptyMessage = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
